/**
 * APIサービス
 */

import type {
  AuthUser,
  ChatRequest,
  ChatResponse,
  ChatHistoryResponse,
  ImageGenerationRequest,
  ImageGenerationResponse,
  SessionInfo,
  FileUploadRequest,
  FileUploadResponse,
  BlocklyFeedbackRequest,
  BlocklyFeedbackResponse,
  ChatMessageRecord,
  ImageRecord,
  UsageLimits,
} from '../types';
import { AgeGroup } from '../types';
import { setCsrfToken, getCsrfToken, createHeaders, clearCsrfToken } from './csrf';

// Re-export for backward compatibility
export { clearCsrfToken };

// 同時に複数のリクエストが403になったとき、再発行を1回にまとめる
let csrfRenewal: Promise<boolean> | null = null;

/**
 * CSRFトークンを再発行して保存する
 *
 * このエンドポイントは既存のCSRFトークンを検証しない（失効しているから
 * 呼ばれるため）。認証はJWTで行われる。
 */
async function renewCsrfToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/csrf`, {
      method: 'POST',
      headers: createHeaders(),
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    const token = data.data?.csrf_token;
    if (!token) {
      return false;
    }

    setCsrfToken(token);
    return true;
  } catch {
    return false;
  }
}

/**
 * CSRFトークンが無ければ発行する（アプリ起動時に1回呼ぶ）
 *
 * トークンの発行は createSession() にぶら下がっているが、既存セッションが
 * あると createSession() は呼ばれない。そのタブはトークンを持たないまま
 * 操作を続け、バックエンドの検証も素通りしていた。
 *
 * トークンが表しているのは user_id であってセッションではないので、
 * 会話の作成ではなく認証が通った時点で確保する。
 *
 * 403後の再発行と同じ Promise を共有し、同時に走らないようにする。
 */
export async function ensureCsrfToken(): Promise<boolean> {
  if (getCsrfToken()) {
    return true;
  }

  csrfRenewal = csrfRenewal ?? renewCsrfToken().finally(() => (csrfRenewal = null));
  return csrfRenewal;
}

/**
 * CSRF保護されたリクエストを送る（403のときトークンを取り替えて1回だけ再送）
 *
 * CSRFトークンは発行から24時間で失効するが、sessionStorage にはタブを閉じる
 * まで残る。セッションが180日残るようになったため、リロードしても既存セッション
 * が選ばれる限りトークンは再発行されず、タブを閉じるまで操作できなくなる。
 *
 * 403 のときはトークンを取り直して再送する。古いトークンを捨てるだけでも
 * バックエンドは通す（トークンが無い場合は Content-Type と Origin のみで
 * 検証する）が、それでは多層防御が1枚外れたまま以降のリクエストが飛ぶ。
 *
 * CSRF検証はハンドラの先頭で行われリクエスト本体は処理されないため、再送で
 * 二重に処理されることはない。
 */
async function fetchWithCsrfRetry(
  url: string,
  init: Omit<RequestInit, 'headers' | 'credentials'>,
  includeJson = true
): Promise<Response> {
  const send = () =>
    fetch(url, { ...init, headers: createHeaders(includeJson), credentials: 'include' });

  const response = await send();
  if (response.status !== 403) {
    return response;
  }

  csrfRenewal = csrfRenewal ?? renewCsrfToken().finally(() => (csrfRenewal = null));
  const renewed = await csrfRenewal;

  // 取り直せなければ元の403をそのまま返す（無意味な再送をしない）
  if (!renewed) {
    return response;
  }

  return send();
}

// 開発環境ではViteのプロキシを使用(/api)、本番環境では環境変数のフルURLを使用
// 本番環境: VITE_API_URLが設定されていればそれを使用（例: https://api.example.com/api）
// 開発環境: Viteプロキシ経由で /api -> backend:3001 に転送
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * 新しいセッションを作成
 */
export async function createSession(): Promise<{
  session_id: string;
  user_id: string;
  age_group: AgeGroup;
  csrf_token?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/session`, {
      method: 'POST',
      headers: createHeaders(),
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`セッション作成失敗 (${response.status}): ${errorText}`);
    }

    const data = await response.json();

    // CSRFトークンを保存（レスポンスは { success, data: { ... } } 形式）
    if (data.data?.csrf_token) {
      setCsrfToken(data.data.csrf_token);
    }

    return data.data;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        'バックエンドに接続できません。Dockerコンテナが起動しているか確認してください。'
      );
    }
    throw error;
  }
}

/**
 * セッションを検証（セキュリティ強化: POSTリクエストに変更）
 */
export async function validateSession(sessionId: string): Promise<{
  valid: boolean;
  session?: SessionInfo;
}> {
  // /auth/validate は成功時に update_session で last_activity と ttl を
  // 書き込む変更系API。期限切れトークンのまま呼ぶと、有効なセッションまで
  // 403 で invalid と判定されるため、再送を挟む
  const response = await fetchWithCsrfRetry(`${API_BASE_URL}/auth/validate`, {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });

  if (!response.ok) {
    return { valid: false };
  }

  const data = await response.json();
  return data.data || { valid: false };
}

/**
 * チャットメッセージを送信
 */
export async function sendMessage(request: ChatRequest): Promise<ChatResponse> {
  const response = await fetchWithCsrfRetry(`${API_BASE_URL}/chat`, {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('メッセージの送信に失敗しました');
  }

  const data = await response.json();
  return data.data;
}

/**
 * 画像を生成
 */
export async function generateImage(
  request: ImageGenerationRequest
): Promise<ImageGenerationResponse> {
  const response = await fetchWithCsrfRetry(`${API_BASE_URL}/image`, {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('画像生成に失敗しました');
  }

  const data = await response.json();
  return data.data;
}

/**
 */
export async function getChatHistory(): Promise<ChatHistoryResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/history`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      return {
        sessions: [],
        user: null,
        unauthenticated: response.status === 401,
        paidPlanRequired: response.status === 402,
      };
    }

    const data = await response.json();
    const sessions: SessionInfo[] = Array.isArray(data.data?.sessions) ? data.data.sessions : [];
    const user: AuthUser | null = data.data?.user ?? null;

    return { sessions, user, unauthenticated: false, paidPlanRequired: false };
  } catch {
    return { sessions: [], user: null, unauthenticated: false, paidPlanRequired: false };
  }
}

/**
 * セッションのメッセージ履歴を取得
 */
export async function getSessionMessages(sessionId: string): Promise<ChatMessageRecord[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/messages/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.data?.messages || [];
  } catch {
    return [];
  }
}

/**
 * セッションを削除（ソフトデリート）
 * @returns { deleted: boolean, notFound: boolean } - notFoundがtrueの場合は既に削除済み（404）
 */
export async function deleteSession(
  sessionId: string
): Promise<{ deleted: boolean; notFound: boolean }> {
  try {
    const response = await fetchWithCsrfRetry(`${API_BASE_URL}/auth/delete/${sessionId}`, {
      method: 'DELETE',
    });

    // 404の場合は既に削除されている（別タブ等で削除済み）
    if (response.status === 404) {
      return { deleted: true, notFound: true };
    }

    if (!response.ok) {
      return { deleted: false, notFound: false };
    }

    const data = await response.json();
    return { deleted: data.data?.deleted || false, notFound: false };
  } catch {
    return { deleted: false, notFound: false };
  }
}

/**
 * セッションの画像履歴を取得
 */
export async function getSessionImages(sessionId: string): Promise<ImageRecord[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/images/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.data?.images || [];
  } catch {
    return [];
  }
}

/**
 * ユーザーの全画像を取得（セッションに関係なく）
 */
export async function getAllImages(): Promise<ImageRecord[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/images`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.data?.images || [];
  } catch {
    return [];
  }
}

/**
 * 画像を削除（ソフトデリート）
 * @returns { deleted: boolean, notFound: boolean } - notFoundがtrueの場合は既に削除済み（404）
 */
export async function deleteImage(
  imageId: string
): Promise<{ deleted: boolean; notFound: boolean }> {
  try {
    const response = await fetchWithCsrfRetry(
      `${API_BASE_URL}/auth/images/delete/${encodeURIComponent(imageId)}`,
      { method: 'DELETE' }
    );

    // 404の場合は既に削除されている（別タブ等で削除済み）
    if (response.status === 404) {
      return { deleted: true, notFound: true };
    }

    if (!response.ok) {
      return { deleted: false, notFound: false };
    }

    const data = await response.json();
    return { deleted: data.data?.deleted || false, notFound: false };
  } catch {
    return { deleted: false, notFound: false };
  }
}

/**
 * ファイルをアップロード
 */
export async function uploadFiles(request: FileUploadRequest): Promise<FileUploadResponse> {
  const payload = {
    session_id: request.session_id,
    files: request.files.map((file) => ({
      data: file.data,
      filename: file.filename,
      mime_type: file.mimeType,
    })),
  };

  const response = await fetchWithCsrfRetry(`${API_BASE_URL}/upload`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('ファイルのアップロードに失敗しました');
  }

  const data = await response.json();
  return data.data;
}

/**
 * 月次のチャット使用状況を取得
 */
let usageLimitsPrefetch: Promise<UsageLimits> | null = null;
let cachedUsageLimits: UsageLimits | null = null;

export async function getUsageLimits(): Promise<UsageLimits> {
  const emptyLimits: UsageLimits = {
    chat: { remaining: null, limit: null, reset_at: null },
  };

  try {
    const response = await fetch(`${API_BASE_URL}/auth/daily-usage`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      cachedUsageLimits = null;
      return emptyLimits;
    }

    const data = await response.json();
    cachedUsageLimits = {
      chat: {
        remaining: data.data?.chat?.remaining ?? null,
        limit: data.data?.chat?.limit ?? null,
        reset_at: data.data?.chat?.reset_at ?? null,
      },
    };
    return cachedUsageLimits;
  } catch {
    cachedUsageLimits = null;
    return emptyLimits;
  }
}

/**
 * 月次使用状況を先読みしてキャッシュする（ローディング画面中に呼ぶ）
 * 取得失敗（空の結果）はキャッシュせず、次回呼び出しで再取得する
 */
export function prefetchUsageLimits(): Promise<UsageLimits> {
  if (!usageLimitsPrefetch) {
    usageLimitsPrefetch = getUsageLimits().then((usage) => {
      if (usage.chat.remaining === null) {
        usageLimitsPrefetch = null;
      }
      return usage;
    });
  }
  return usageLimitsPrefetch;
}

/**
 * キャッシュ済みの月次使用状況を返す（未取得・取得失敗時は null）
 */
export function getCachedUsageLimits(): UsageLimits | null {
  return cachedUsageLimits;
}

/**
 * APIレスポンスに含まれる残数情報でキャッシュを更新する（消費との同期用）
 */
export function updateCachedUsageLimits(update: { chat?: UsageLimits['chat'] }): void {
  if (!cachedUsageLimits) return;
  cachedUsageLimits = {
    chat: update.chat ?? cachedUsageLimits.chat,
  };
}

/**
 * 使い方モーダルの確認状態を取得
 */
export async function getUsageGuideStatus(): Promise<{ acknowledged: boolean }> {
  const response = await fetch(`${API_BASE_URL}/auth/usage-guide`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch usage guide status: ${response.status}`);
  }

  const data = await response.json();
  return { acknowledged: data.data?.acknowledged === true };
}

/**
 * 使い方モーダルを確認したことを記録
 */
export async function acknowledgeUsageGuide(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/usage-guide/acknowledge`, {
    method: 'POST',
    headers: createHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to acknowledge usage guide: ${response.status}`);
  }
}

/**
 * Blocklyプロジェクトのフィードバックを取得
 */
export async function getBlocklyFeedback(
  request: BlocklyFeedbackRequest
): Promise<BlocklyFeedbackResponse> {
  try {
    const response = await fetchWithCsrfRetry(`${API_BASE_URL}/blockly/feedback`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Blocklyフィードバックの取得に失敗しました (${response.status}): ${errorText}`
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('バックエンドに接続できません。サーバーが起動しているか確認してください。');
    }
    throw error;
  }
}
